import { Model } from '../../infra/model'
import {
  RoomDto,
  RoomStatusDto,
  RoomSessionDto,
  RoomSourceDto,
} from '../dtos/rooms'
import { Id } from '../../types/definitions'
import once from 'lodash/once'
import Conversation from '../models/Conversation'
import Member from '../../iam/models/Member'
import { DateZero } from '../../utils/date-util'
import { PrimeMemberByPublicId } from '../../iam/MemberStore'
import { RoomSession } from './RoomSession'
import { collection } from '../../infra/collection'
import { makeObservable, observable } from 'mobx'

export class Room extends Model<RoomDto, Room> {
  id = Id.none
  dateCreated = DateZero()
  dateModified = DateZero()
  status = RoomStatus.Unknown
  roomSource: RoomSource = {
    type: 'Unknown',
  }

  sessions = collection<RoomSessionDto, RoomSession>({
    idAttribute: 'id',
    create: () =>
      new RoomSession(this.conversation.id, this.primeMemberByPublicId),
  })

  activeParticipants = collection<{ user_public_id: string }, Member>({
    idAttribute: 'user_public_id',
    create: (attrs) =>
      this.primeMemberByPublicId({
        workspace_id: this.conversation.workspace.id,
        user_public_id: attrs.user_public_id,
      }),
  })

  static get none(): Room {
    return lazyNone()
  }

  constructor(
    public conversation: Conversation,
    private primeMemberByPublicId: PrimeMemberByPublicId,
  ) {
    super()
    makeObservable(this, {
      id: observable,
      dateCreated: observable,
      dateModified: observable,
      status: observable,
      roomSource: observable,
    })
  }

  parse(attrs: Partial<RoomDto>): Partial<Room> {
    return {
      id: attrs.id,
      dateCreated: attrs.date_created,
      dateModified: attrs.date_modified,
      activeParticipants: this.activeParticipants.reset(
        attrs.active_participants?.map((p) => ({ user_public_id: p })),
      ),
      status: Room.mapRoomStatus(attrs.status),
      sessions: this.sessions.reset(attrs.sessions),
      roomSource: Room.mapRoomSource(attrs.room_source),
    }
  }

  private static mapRoomStatus(status: RoomStatusDto | undefined): RoomStatus {
    switch (status) {
      case RoomStatusDto.Active:
        return RoomStatus.Active
      case RoomStatusDto.Closed:
        return RoomStatus.Closed
      default:
        return RoomStatus.Unknown
    }
  }

  private static mapRoomSource(source: RoomSourceDto | undefined): RoomSource {
    switch (source?.type) {
      case 'Whereby':
        return {
          type: 'Whereby',
        }
      case 'Local':
        return {
          type: 'Local',
        }
      default:
        return {
          type: 'Unknown',
        }
    }
  }
}

export const enum RoomStatus {
  Unknown = 'Unknown',
  Active = 'Active',
  Closed = 'Closed',
}

export type RoomSource = WherebyRoomSource | LocalRoomSource | UnknownRoomSource

export interface LocalRoomSource {
  type: 'Local'
}

export interface WherebyRoomSource {
  type: 'Whereby'
}

export interface UnknownRoomSource {
  type: 'Unknown'
}

const lazyNone = once(() => new Room(Conversation.none, () => Member.none))
